import React from 'react';
import { Col, Row } from 'reactstrap';
import { FormFieldHeader } from '../../../../../../../components/FormFieldHeader';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  useFormState,
} from 'react-hook-form';
import { SelectOption } from '../../../../../../../components/Select';
import { HrisLocationService } from '../../../../../../../services/v1/applicant_tracking/HrisLocationService';
import { SinglePaginatedSelect } from '../../../../../../../components/Select/SinglePaginatedSelect';
import { PushToHrisFormValues } from '../../..';
import { InputFormField } from '../../../../../../../components/InputFormField';
import classNames from 'classnames';
import { hrisFieldWarningTooltip, isFreeText } from '../../../hrisFieldWarning';

interface PropTypes {
  register: UseFormRegister<PushToHrisFormValues>;
  errors: FieldErrors;
  control: Control<PushToHrisFormValues>;
  hrisProviderLabel: string;
  officeSuggestionLabel: string | null;
}

interface AdditionalType {
  page: number;
}

const defaultAdditional: AdditionalType = {
  page: 1,
};

function OfficeSelect(props: {
  selected: SelectOption;
  onChange: (opt: SelectOption) => void;
}) {
  const loadOptions = async (
    search: string,
    _loadedOptions: unknown,
    { page }: AdditionalType,
  ) => {
    const result = await HrisLocationService.list({
      page: page,
      name: search,
    });

    return {
      options: result.hrisLocations.map(
        (location) =>
          ({
            label: location.name,
            value: location.externalId ?? location.id.toString(),
          } as SelectOption),
      ),
      hasMore: !!result.nextPage,
      additional: {
        page: page + 1,
      },
    };
  };

  return (
    <SinglePaginatedSelect
      additional={defaultAdditional}
      isClearable={false}
      value={props.selected}
      loadOptions={loadOptions}
      onChange={props.onChange}
      debounceTimeout={300}
      placeholder='Select Office'
      closeMenuOnSelect={true}
    />
  );
}

export function OfficeAndWorkEmailRow(props: PropTypes) {
  const { dirtyFields } = useFormState({ control: props.control });
  const officeDirty = Boolean(dirtyFields.office);

  return (
    <Row className='mb-3 align-items-start'>
      <Col sm={6}>
        <Controller
          name='office'
          control={props.control}
          rules={{ required: 'Office is required' }}
          render={({ field }) => {
            return (
              <>
                <FormFieldHeader
                  fieldName={'Office'}
                  isRequired={true}
                  tooltipText={hrisFieldWarningTooltip({
                    currentLabel: field.value?.label,
                    suggestionLabel: props.officeSuggestionLabel,
                    freeText: isFreeText(field.value),
                    dirty: officeDirty,
                    fieldLabel: 'office name',
                  })}
                  tooltipVariant='warning'
                  tooltipPlaceholder={true}
                />
                <OfficeSelect
                  selected={field.value}
                  onChange={field.onChange}
                />
              </>
            );
          }}
        />
      </Col>
      <Col sm={6}>
        <InputFormField
          type={'text'}
          registerReturn={props.register('workEmail', {
            required: true,
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Invalid email address',
            },
          })}
          fieldName={'Work Email'}
          placeholder={'Insert Email'}
          isRequired={true}
          classNames={classNames(props.errors.email && 'is-invalid', 'fs-5')}
          tooltipText={
            'A work email is required by your HRIS. Please update if one has already been created.'
          }
        />
      </Col>
    </Row>
  );
}
